<script setup lang="ts">
import { computed } from 'vue';
import { NEXT_DAY, scheduleColor } from '@/utils/customPlaylistSchedule';
import ScheduleDayChips from './ScheduleDayChips.vue';
import ScheduleTypeIcon from './ScheduleTypeIcon.vue';
import type { ScheduleEntry } from './scheduleGroups';

/**
 * One schedule of a custom list, in one line: the colour rail, the type icon,
 * the day chips and the time. It uses the same colours and icons as the
 * search card's schedule lines, so a list reads the same in either layout.
 *
 * The table's Schedules column gives it each schedule in turn, through
 * ScheduleOneLinerList. The list has already summarised each schedule on its
 * own zone's clock, so this line only draws the entry it is given.
 */
const props = withDefaults(
    defineProps<{
        entry: ScheduleEntry;
        /**
         * Put the schedule's full text in a native title. Off where the caller
         * already shows a tooltip over the line, as the table cell does.
         */
        withTitle?: boolean;
    }>(),
    { withTitle: true },
);

const timeText = computed(() => {
    const summary = props.entry.summary;
    return [summary.days === null ? summary.dateWindow : null, summary.timeRange].filter(Boolean).join(' ');
});
</script>

<template>
    <span
        class="flex min-w-0 items-center gap-1.5 self-stretch"
        :title="withTitle ? entry.text : undefined"
        data-testid="schedule-one-liner"
    >
        <span
            class="min-h-[11px] w-1 shrink-0 self-stretch rounded-sm"
            :style="{ backgroundColor: scheduleColor(entry.schedule) }"
            aria-hidden="true"
        />
        <ScheduleTypeIcon :kind="entry.summary.kind" />
        <ScheduleDayChips v-if="entry.summary.days !== null" :days="entry.summary.days" />
        <span
            class="min-w-0 truncate tabular-nums"
            :class="entry.summary.state === 'expired' ? 'line-through' : ''"
            data-testid="container-schedules-text"
            >{{ timeText }}<span v-if="entry.summary.crossesMidnight" data-testid="schedule-next-day">{{ ` ${NEXT_DAY}` }}</span></span
        >
    </span>
</template>
